/**
 * Shared Site Configuration Types & Defaults
 * ===========================================
 * This file is safe to import from both client and server components.
 * It contains ONLY types and default values — no server-only imports.
 *
 * The main siteConfig.ts re-exports everything from here, so existing
 * imports like `import { SiteConfig } from '@/lib/config/siteConfig'`
 * continue to work for server components.
 *
 * Client components (like SiteConfigContext) should import from this file
 * to avoid pulling in next/headers through the import chain.
 */

// ============================================
// TYPE DEFINITIONS
// ============================================
export interface SiteConfig {
  // Company Identity
  companyName: string;
  companyLegalName: string;
  tagline: string;
  description: string;

  // Logo & Branding
  logoUrl: string;
  logoAltText: string;
  faviconUrl: string;

  // Contact Information
  phone: string;
  phoneRaw: string;
  phoneInternational: string;
  email: {
    sales: string;
    support: string;
    warranty: string;
    notifications: string;
    noreply: string;
  };

  // Address
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    full: string;
  };

  // Business Hours
  hours: {
    weekday: string;
    saturday: string;
    sunday: string;
    full: string;
    timezone: string;
  };

  // Theme Colors
  theme: {
    primaryColor: string;
    primaryDark: string;
    primaryLight: string;
    accentColor: string;
    accentHover: string;
    secondaryColor: string;
    // Gray palette for backgrounds, text, borders - admin controllable
    grayPalette?: {
      gray50: string;   // Background primary (warm ivory)
      gray100: string;  // Background alt (cream)
      gray200: string;  // Linen
      gray300: string;  // Border color
      gray400: string;  // Muted
      gray500: string;  // Medium gray
      gray600: string;  // Text muted
      gray700: string;  // Text secondary
      gray800: string;  // Dark
      gray900: string;  // Text primary (charcoal)
      gray950: string;  // Near black
    };
  };

  // Fonts
  fonts: {
    primary: string;
    secondary: string;
  };

  // Social Media
  social: {
    facebook: string;
    instagram: string;
    twitter: string;
    youtube: string;
    pinterest: string;
    linkedin: string;
  };

  // SEO
  seo: {
    defaultTitle: string;
    titleSuffix: string;
    defaultDescription: string;
    defaultKeywords: string;
    ogImage: string;
  };

  // Domain & URLs
  siteUrl: string;
  domain: string;

  // Analytics
  googleAnalyticsId: string;
  facebookPixelId: string;

  // Feature Flags
  features: {
    showComingSoonBanner: boolean;
    maintenanceMode: boolean;
    enableReviews: boolean;
    enableWishlist: boolean;
    enableCompare: boolean;
  };
}

// ============================================
// DEFAULT CONFIGURATION (Must be configured in database)
// All values must come from company_settings table
// ============================================
export const DEFAULT_SITE_CONFIG: SiteConfig = {
  // Company Identity - must be configured in database
  companyName: '',
  companyLegalName: '',
  tagline: '',
  description: '',

  // Logo & Branding - must be configured in database
  logoUrl: '/images/logo-optimized.webp',
  logoAltText: '',
  faviconUrl: '/favicon.ico',

  // Contact Information - must be configured in database
  phone: '',
  phoneRaw: '',
  phoneInternational: '',
  email: {
    sales: '',
    support: '',
    warranty: '',
    notifications: '',
    noreply: '',
  },

  // Address - must be configured in database
  address: {
    street: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    full: '',
  },

  // Business Hours - must be configured in database
  hours: {
    weekday: '',
    saturday: '',
    sunday: '',
    full: '',
    timezone: '',
  },

  // Theme Colors - must be configured in database
  theme: {
    primaryColor: '',
    primaryDark: '',
    primaryLight: '',
    accentColor: '',
    accentHover: '',
    secondaryColor: '',
    grayPalette: {
      gray50: '',
      gray100: '',
      gray200: '',
      gray300: '',
      gray400: '',
      gray500: '',
      gray600: '',
      gray700: '',
      gray800: '',
      gray900: '',
      gray950: '',
    },
  },

  // Fonts - must be configured in database
  fonts: {
    primary: '',
    secondary: '',
  },

  // Social Media - must be configured in database
  social: {
    facebook: '',
    instagram: '',
    twitter: '',
    youtube: '',
    pinterest: '',
    linkedin: '',
  },

  // SEO - must be configured in database
  seo: {
    defaultTitle: '',
    titleSuffix: '',
    defaultDescription: '',
    defaultKeywords: '',
    ogImage: '',
  },

  // Domain & URLs (from environment variable)
  siteUrl: process.env.NEXT_PUBLIC_SITE_URL || 'http://localhost:3000',
  domain: '',

  // Analytics
  googleAnalyticsId: process.env.NEXT_PUBLIC_GA_ID || '',
  facebookPixelId: process.env.NEXT_PUBLIC_FB_PIXEL_ID || '',

  // Feature Flags
  features: {
    showComingSoonBanner: true,
    maintenanceMode: false,
    enableReviews: true,
    enableWishlist: true,
    enableCompare: true,
  },
};
